// Per-task resolution of language-use grading: every language-use rule is a LanguageUseScheme (Exam.languageUseSchemeRules),
// whether it covers exactly one task ("solo") or several ("group") — or the task falls back to the
// Fehlerindex (FI) when it has no configured rule at all. Both the server grading pipeline and the
// client summary/canvas panels use these helpers so their decisions can never disagree. Kept
// client-safe (no server-only imports) — only plain, already-loaded data in, plain values out.

import { GRAMMAR_TASK_TYPES } from './grammar-grading.js';

export interface LanguageUseSchemeLike {
  _id?: string | null;
  criteria: string;
  maxPoints?: number | null;
  confidence?: number | null;
  isApproved?: boolean | null;
  carriedOverFromGroup?: boolean | null;
  dependsOn: string[];
  /** Teacher/chat-facing "Gruppe N" label — absent on a rule written before this field existed. */
  displayNumber?: number | null;
}

export interface ExamTaskLike {
  taskNumber: string;
  taskType?: string | null;
  subTasks?: ExamTaskLike[] | null;
}

export interface LanguageUseRuleResultLike {
  _id?: string;
  languageUseSchemeId: string;
  score: number;
  explanation: string;
  confidence: number;
  reviewerFeedback?: string | null;
  isApproved?: boolean | null;
  actualPointsGiven?: number | null;
}

/** Which mechanism governs a task's grammar-eligible grading: a configured rule, the Fehlerindex fallback, or neither. */
export type TaskGrammarMode = 'none' | 'fi' | 'languageUse';

export interface SoloLanguageUseRule {
  schemeId: string;
  criteria: string;
  maxPoints: number;
  confidence: number | null;
  isApproved: boolean | null;
  carriedOverFromGroup: boolean | null;
}

/**
 * EVERY exam-level LanguageUseScheme this task belongs to. A task can belong to more than one rule, so
 * this returns all matches, not just the first.
 */
export function findLanguageUseSchemesForTask(
  taskNumber: string | null | undefined,
  languageUseSchemeRules: LanguageUseSchemeLike[] | null | undefined,
): LanguageUseSchemeLike[] {
  const tn = String(taskNumber ?? '').trim();
  if (!tn) return [];
  return (languageUseSchemeRules ?? []).filter(scheme => Array.isArray(scheme?.dependsOn) && scheme.dependsOn.includes(tn));
}

/**
 * The single-member ("solo") LanguageUseScheme covering this task, if any — a rule with exactly one
 * dependsOn entry is graded inline with the task's content call (batched, no dedicated LLM call),
 * exactly like the old own-criteria mechanism, whereas a 2+-member rule is graded via
 * gradeLanguageUseSchemeGroup. This is the one place that distinction is decided.
 */
export function getSoloLanguageUseRule(
  taskNumber: string | null | undefined,
  languageUseSchemeRules: LanguageUseSchemeLike[] | null | undefined,
): SoloLanguageUseRule | null {
  const solo = findLanguageUseSchemesForTask(taskNumber, languageUseSchemeRules).find(
    scheme => Array.isArray(scheme?.dependsOn) && scheme.dependsOn.length === 1,
  );
  const criteria = String(solo?.criteria ?? '').trim();
  if (!solo || !criteria) return null;
  return {
    schemeId: String(solo._id ?? ''),
    criteria,
    maxPoints: Number(solo.maxPoints) || 0,
    confidence: solo.confidence ?? null,
    isApproved: solo.isApproved ?? null,
    carriedOverFromGroup: solo.carriedOverFromGroup ?? null,
  };
}

/** A LanguageUseScheme.dependsOn entry is a "|"-joined qualified path (e.g. "2|a") for matching — never
 * shown to a teacher as-is. Use this to render it back into the printed dotted style ("2.a") for any
 * teacher-facing label (dependsOn.join(', ') and similar). */
export function formatTaskReferenceForDisplay(taskReference: string): string {
  return taskReference.replace(/\|/g, '.');
}

export interface LanguageUseBoxSource {
  /** Every rule is a LanguageUseScheme now (solo or multi-member) — this is always 'group'. */
  kind: 'group';
  /** The LanguageUseScheme._id. */
  key: string;
  dependsOn: string[];
  /** Teacher/chat-facing "Gruppe N" label — null on a rule written before this field existed. */
  displayNumber: number | null;
}

/**
 * Client-only helper for the grading canvas / MS-GS canvas box-mode toggle: every language-use rule this
 * task belongs to (a task can belong to more than one). Empty when the task has no language-use rule at
 * all, in which case the toggle stays hidden.
 */
export function resolveLanguageUseBoxSources(
  taskNumber: string | null | undefined,
  languageUseSchemeRules: LanguageUseSchemeLike[] | null | undefined,
): LanguageUseBoxSource[] {
  return findLanguageUseSchemesForTask(taskNumber, languageUseSchemeRules).map(scheme => ({
    kind: 'group' as const,
    key: String(scheme._id ?? ''),
    dependsOn: scheme.dependsOn ?? [],
    displayNumber: typeof scheme.displayNumber === 'number' ? scheme.displayNumber : null,
  }));
}

/** True when this task has any configured language-use rule (solo or group). */
export function taskHasLanguageUseRules(
  taskNumber: string | null | undefined,
  languageUseSchemeRules: LanguageUseSchemeLike[] | null | undefined,
): boolean {
  return findLanguageUseSchemesForTask(taskNumber, languageUseSchemeRules).length > 0;
}

/**
 * The exam-level override (Exam.grammarFiSettingsJson.alwaysUseFi) wins when explicitly set
 * (true/false); otherwise falls back to the teacher's default (Teacher.grammarPreferences.alwaysUseFi).
 */
export function resolveAlwaysUseFi(
  examAlwaysUseFi: boolean | null | undefined,
  teacherAlwaysUseFi: boolean | null | undefined,
): boolean {
  if (typeof examAlwaysUseFi === 'boolean') return examAlwaysUseFi;
  return Boolean(teacherAlwaysUseFi);
}

/**
 * Per-task grammar mode: 'languageUse' whenever the task has an actually configured rule (solo or
 * multi-member LanguageUseScheme) — configured rules always win, regardless of the alwaysUseFi override
 * (exam or teacher default): a teacher who has explicitly set up rules for a task wants them used,
 * not silently bypassed by a generic default. Otherwise 'fi' when the task type is grammar-eligible
 * (there's no rule to use, so Fehlerindex is the only option), else 'none'.
 */
export function taskGrammarMode(
  examTask: ExamTaskLike | null | undefined,
  taskNumber: string | null | undefined,
  languageUseSchemeRules: LanguageUseSchemeLike[] | null | undefined,
): TaskGrammarMode {
  const hasLanguageUseRules = taskHasLanguageUseRules(taskNumber, languageUseSchemeRules);
  if (hasLanguageUseRules) return 'languageUse';
  const isGrammarEligibleTask = GRAMMAR_TASK_TYPES.has(String(examTask?.taskType ?? ''));
  return isGrammarEligibleTask ? 'fi' : 'none';
}

/** Resolves every group a task belongs to, then looks up each matching Submission.languageUseRuleResults entry. */
export function findLanguageUseRuleResultsForTask(
  taskNumber: string | null | undefined,
  languageUseSchemeRules: LanguageUseSchemeLike[] | null | undefined,
  languageUseRuleResults: LanguageUseRuleResultLike[] | null | undefined,
): LanguageUseRuleResultLike[] {
  const schemes = findLanguageUseSchemesForTask(taskNumber, languageUseSchemeRules);
  if (schemes.length === 0) return [];
  const schemeIds = new Set(schemes.map(s => s._id).filter(Boolean));
  return (languageUseRuleResults ?? []).filter(r => schemeIds.has(r.languageUseSchemeId));
}

/** Solo-rule max points for one task, clamped to its own max — or null if the task has none. */
export function resolveSoloLanguageUseMaxPoints(
  taskNumber: string | null | undefined,
  languageUseSchemeRules: LanguageUseSchemeLike[] | null | undefined,
): number | null {
  const solo = getSoloLanguageUseRule(taskNumber, languageUseSchemeRules);
  return solo ? solo.maxPoints : null;
}

/**
 * Solo-rule points (max + earned) for one task, resolved from its LanguageUseRuleResult entry (the same
 * array/upsert a multi-member group's result lives in — solo results are unified into it too), or
 * null if the task has no solo language-use rule.
 */
export function resolveSoloLanguageUsePoints(
  taskNumber: string | null | undefined,
  languageUseSchemeRules: LanguageUseSchemeLike[] | null | undefined,
  languageUseRuleResults: LanguageUseRuleResultLike[] | null | undefined,
): { maxPoints: number; earnedPoints: number } | null {
  const solo = getSoloLanguageUseRule(taskNumber, languageUseSchemeRules);
  if (!solo) return null;
  const result = (languageUseRuleResults ?? []).find(r => String(r?.languageUseSchemeId ?? '') === solo.schemeId);
  return resolveLanguageUseGroupPoints({ maxPoints: solo.maxPoints, dependsOn: [], criteria: solo.criteria }, result);
}

/** Group points (max + earned) for one LanguageUseScheme group and its matching shared result. */
export function resolveLanguageUseGroupPoints(
  scheme: LanguageUseSchemeLike,
  result: LanguageUseRuleResultLike | null | undefined,
): { maxPoints: number; earnedPoints: number } {
  const maxPoints = Math.max(0, Number(scheme?.maxPoints) || 0);
  const raw = result?.actualPointsGiven ?? result?.score ?? 0;
  return { maxPoints, earnedPoints: Math.max(0, Math.min(Number(raw) || 0, maxPoints)) };
}
